import {
  isAccessDeniedHelper,
  isContactWriteAllowedHelper,
  isReadAllAllowedHelper,
  isReadAllowedHelper,
  isReadMapAllowedHelper,
  isTabVisible,
  isWriteAllAllowedHelper,
  isWriteAllowedHelper,
} from '../../common/helpers/permissions.helpers';
import {
  FETCH_ADMIN_EMAILS,
  FETCH_ADMIN_EMAILS_FAILED,
  FETCH_ADMIN_EMAILS_SUCCESS,
  FETCH_EMAIL_GROUPS,
  FETCH_EMAIL_GROUPS_FAILED,
  FETCH_EMAIL_GROUPS_SUCCESS,
  FETCH_TAB_VISIBILITY,
  FETCH_TAB_VISIBILITY_FAILED,
  FETCH_TAB_VISIBILITY_SUCCESS,
  FETCH_USER_EMAIL_GROUPS,
  FETCH_USER_EMAIL_GROUPS_FAILED,
  FETCH_USER_EMAIL_GROUPS_SUCCESS,
  FETCH_USER_POSITIONS,
  FETCH_USER_POSITIONS_FAILED,
  FETCH_USER_POSITIONS_SUCCESS,
  SET_USER_ROLES,
  UPDATE_USER_EMAIL_GROUPS,
  UPDATE_USER_EMAIL_GROUPS_FAILED,
  UPDATE_USER_EMAIL_GROUPS_SUCCESS,
} from './user-actions';

export const USER_STATE_KEY = 'USER_REDUCER';

export interface IUserEmail {
  id: number | null;
  email: string;
  emailGroups: string[];
  adminEmails: string[];
  emailSubGroups: string[];
}

export interface IUserState {
  roles: string[];
  isReadAllAllowed: boolean;
  isWriteAllAllowed: boolean;
  readTabsPermissions: {
    locations: boolean;
    profile: boolean;
    finance: boolean;
    marketing: boolean;
    schedule: boolean;
    mvc: boolean;
    pro: boolean;
    it: boolean;
    telecom: boolean;
    veh: boolean;
    merchandising: boolean;
    specialty: boolean;
    map: boolean;
    doctors: boolean;
    vendor: boolean;
    pxp: boolean;
    callForwarding: boolean;
  };
  writeTabsPermissions: {
    locations: boolean;
    profile: boolean;
    finance: boolean;
    marketing: boolean;
    schedule: boolean;
    mvc: boolean;
    pro: boolean;
    it: boolean;
    telecom: boolean;
    veh: boolean;
    merchandising: boolean;
    specialty: boolean;
    map: boolean;
    doctors: boolean;
    vendor: boolean;
    pxp: boolean;
    callForwarding: boolean;
  };
  writeMenuPermissions: { [menu: string]: boolean };
  isAccessDenied: boolean;
  loading: boolean;
  emailGroups: string[];
  adminEmails: string[];
  userEmail: IUserEmail;
  tabVisibility: { tab: string; authorized: boolean }[];
  userPositions: string[];
  userPositionsLoading: boolean;
  permittedMenus: string[];
  permittedMenuTabs: string[]; // stored as `Menu|Tab`
  permittedFeatures: string[];
}

const defaultState: IUserState = {
  roles: [],
  isReadAllAllowed: false,
  isWriteAllAllowed: false,
  readTabsPermissions: {
    locations: false,
    profile: false,
    finance: false,
    marketing: false,
    schedule: false,
    mvc: false,
    pro: false,
    it: false,
    telecom: false,
    veh: false,
    merchandising: false,
    specialty: false,
    map: false,
    doctors: false,
    vendor: false,
    pxp: false,
    callForwarding: false,
  },
  writeTabsPermissions: {
    locations: false,
    profile: false,
    finance: false,
    marketing: false,
    schedule: false,
    mvc: false,
    pro: false,
    it: false,
    telecom: false,
    veh: false,
    merchandising: false,
    specialty: false,
    map: false,
    doctors: false,
    vendor: false,
    pxp: false,
    callForwarding: false,
  },
  writeMenuPermissions: {},
  isAccessDenied: true,
  loading: false,
  emailGroups: [],
  adminEmails: [],
  userEmail: {
    id: null,
    email: '',
    emailGroups: [],
    adminEmails: [],
    emailSubGroups: [],
  },
  tabVisibility: [],
  userPositions: [],
  userPositionsLoading: false,
  permittedMenus: [],
  permittedMenuTabs: [],
  permittedFeatures: [],
};

export const userReducer = (state = defaultState, { type, payload }: any) => {
  switch (type) {
    case SET_USER_ROLES:
      return {
        ...state,
        roles: payload,
        isReadAllAllowed: isReadAllAllowedHelper(payload),
        isWriteAllAllowed: isWriteAllAllowedHelper(payload),
        readTabsPermissions: {
          locations: isReadAllowedHelper('locations', payload),
          profile: isReadAllowedHelper('profile', payload),
          finance: isReadAllowedHelper('finance', payload),
          marketing: isReadAllowedHelper('marketing', payload),
          schedule: isReadAllowedHelper('schedule', payload),
          mvc: isReadAllowedHelper('mvc', payload),
          pro: isReadAllowedHelper('pro', payload),
          it: isReadAllowedHelper('it', payload),
          veh: isReadAllowedHelper('veh', payload),
          merchandising: isReadAllowedHelper('merchandising', payload),
          telecom: isReadAllowedHelper('telecom', payload),
          specialty: isReadAllowedHelper('specialty', payload),
          vendor: isReadAllowedHelper('vendor', payload),
          map: isReadMapAllowedHelper(payload),
          callForwarding: isReadAllowedHelper('callForwarding', payload),
        },
        writeTabsPermissions: {
          locations: isWriteAllowedHelper('locations', payload),
          profile: isWriteAllowedHelper('profile', payload),
          finance: isWriteAllowedHelper('finance', payload),
          marketing: isWriteAllowedHelper('marketing', payload),
          schedule: isWriteAllowedHelper('schedule', payload),
          mvc: isWriteAllowedHelper('mvc', payload),
          pro: isWriteAllowedHelper('pro', payload),
          it: isWriteAllowedHelper('it', payload),
          veh: isWriteAllowedHelper('veh', payload),
          merchandising: isWriteAllowedHelper('merchandising', payload),
          telecom: isWriteAllowedHelper('telecom', payload),
          specialty: isWriteAllowedHelper('specialty', payload),
          vendor: isWriteAllowedHelper('vendor', payload),
          map: isReadMapAllowedHelper(payload),
          callForwarding: isWriteAllowedHelper('callForwarding', payload),
        },
        isReadDoctorsAllowed: isReadAllowedHelper('doctors', payload),
        isWriteDoctorsAllowed: isWriteAllowedHelper('doctors', payload),
        isAccessDenied: isAccessDeniedHelper(payload),
        isWriteContactAllowed: isContactWriteAllowedHelper('contact', payload),
      };
    case FETCH_TAB_VISIBILITY:
      return {
        ...state,
        loading: true,
      };
    case FETCH_TAB_VISIBILITY_SUCCESS:
      return {
        ...state,
        loading: false,
        tabVisibility: payload,
        writeTabsPermissions: {
          ...state.writeTabsPermissions,
          // pxp: isTabVisible('pxp', payload) && isWriteAllowedHelper('pxp', state.roles),
        },
        readTabsPermissions: {
          ...state.readTabsPermissions,
          // pxp: isTabVisible('pxp', payload) && isReadAllowedHelper('pxp', state.roles),
        },
      };
    case FETCH_TAB_VISIBILITY_FAILED:
      return {
        ...state,
        loading: false,
      };
    case FETCH_EMAIL_GROUPS:
    case FETCH_ADMIN_EMAILS:
      return {
        ...state,
        loading: true,
      };
    case FETCH_EMAIL_GROUPS_SUCCESS:
      return {
        ...state,
        loading: false,
        emailGroups: payload,
      };
    case FETCH_EMAIL_GROUPS_FAILED:
    case FETCH_ADMIN_EMAILS_FAILED:
      return {
        ...state,
        loading: false,
      };
    case FETCH_ADMIN_EMAILS_SUCCESS:
      return {
        ...state,
        loading: false,
        adminEmails: payload,
      };
    case FETCH_USER_EMAIL_GROUPS:
      return {
        ...state,
        userEmail: { ...state.userEmail, email: payload },
        loading: true,
      };
    case FETCH_USER_EMAIL_GROUPS_SUCCESS:
      return {
        ...state,
        loading: false,
        userEmail: {
          ...state.userEmail,
          id: payload?.id,
          emailGroups: payload ? payload.emailGroups : [],
          emailSubGroups: payload ? payload.emailSubGroups : [],
        },
      };
    case FETCH_USER_EMAIL_GROUPS_FAILED:
      return {
        ...state,
        loading: false,
      };
    case UPDATE_USER_EMAIL_GROUPS:
      return {
        ...state,
        loading: true,
      };
    case UPDATE_USER_EMAIL_GROUPS_SUCCESS:
      return {
        ...state,
        loading: false,
        userEmail: payload,
      };
    case UPDATE_USER_EMAIL_GROUPS_FAILED:
      return {
        ...state,
        loading: false,
      };
    case FETCH_USER_POSITIONS:
    case FETCH_USER_POSITIONS_FAILED:
      return {
        ...state,
        userPositions: [],
        userPositionsLoading: type === FETCH_USER_POSITIONS ? true : false,
      };
    case FETCH_USER_POSITIONS_SUCCESS: {
      const perms = (payload && payload.Permissions) || [];

      // helper to split DisplayName by '->'
      const partsOf = (displayName: string) =>
        (displayName || '')
          .split('->')
          .map((s) => s.trim())
          .filter(Boolean);

      const menus = new Set<string>();
      const tabs = new Set<string>(); // key: `${menu}|${tab}`
      const features = new Set<string>();
      const menuWriteMap: Record<string, boolean> = {};
      let hasRead = false;
      let hasWrite = false;

      perms.forEach((p: any) => {
        const parts = partsOf(p.DisplayName || p.Name || '');
        if (parts.length === 3) {
          const [menu, tab, perm] = parts;
          menus.add(menu);
          tabs.add(`${menu}|${tab}`);
          if (/read/i.test(perm)) hasRead = true;
          if (/write/i.test(perm)) hasWrite = true;
        } else if (parts.length === 2) {
          const [menu, perm] = parts;
          menus.add(menu);
          // collect menu-level write permissions separately
          if (/read/i.test(perm)) hasRead = true;
          if (/write/i.test(perm)) {
            hasWrite = true;
            menuWriteMap[(menu || '').toLowerCase()] = true;
          }
        } else if (parts.length === 1) {
          const [single] = parts;
          features.add(single);
        }
      });

      // mapping of app tab keys -> substrings to look for in DisplayName tab part
      const TAB_MATCHERS: Record<string, string[]> = {
        locations: ['Location_And_Managers'],
        profile: ['Profile'],
        finance: ['Finance'],
        marketing: ['Marketing'],
        schedule: ['Schedule'],
        mvc: ['MVC'],
        pro: ['Pro_Services'],
        it: ['IT_Services'],
        telecom: ['Telecom'],
        veh: ['Veh'],
        merchandising: ['Merchandising'],
        specialty: ['Specialty'],
        map: ['Map'],
        doctors: ['Doctors'],
        vendor: ['Vendor'],
        pxp: ['PXP'],
        standard: ['Standard'],
        unplanned: ['Unplanned'],
        callForwarding: ['Call_Forwarding'],
      };

      // More robust per-tab detection: scan individual permissions for menu/tab/read/write
      const newReadTabsPermissions: any = { ...state.readTabsPermissions };
      const newWriteTabsPermissions: any = { ...state.writeTabsPermissions };

      Object.keys(TAB_MATCHERS).forEach((tabKey) => {
        const matchers = TAB_MATCHERS[tabKey].map((s) => s.toLowerCase());
        const menuCandidates = ['Offices', 'HOO_Accommodations'];

        if ((matchers && matchers.length === 0) || (menuCandidates && menuCandidates.length === 0)) return;
        newReadTabsPermissions[tabKey] = perms.some(
          (p: any) =>
            p.Name.toLowerCase() === `${menuCandidates[0]}_${matchers[0]}_Read`.toLocaleLowerCase() ||
            p.Name.toLowerCase() === `${menuCandidates[1]}_${matchers[0]}_Read`.toLocaleLowerCase()
        );
        newWriteTabsPermissions[tabKey] = perms.some(
          (p: any) =>
            p.Name.toLowerCase() === `${menuCandidates[0]}_${matchers[0]}_Write`.toLocaleLowerCase() ||
            p.Name.toLowerCase() === `${menuCandidates[1]}_${matchers[0]}_Write`.toLocaleLowerCase()
        );
      });

      if (payload.IsMODAdmin) {
        newReadTabsPermissions.merchandising = true;
        newWriteTabsPermissions.merchandising = true;
      }

      const isAccessDenied = !payload.IsMODAdmin && perms.length === 0;

      const newWriteMenuPermissions = Object.keys(menuWriteMap).reduce((acc: any, key) => {
        acc[key] = true;
        return acc;
      }, {} as any);

      return {
        ...state,
        userPositions: payload,
        userPositionsLoading: false,
        readTabsPermissions: newReadTabsPermissions,
        writeTabsPermissions: newWriteTabsPermissions,
        writeMenuPermissions: newWriteMenuPermissions,
        isAccessDenied,
        permittedMenus: Array.from(menus),
        permittedMenuTabs: Array.from(tabs),
        permittedFeatures: Array.from(features),
      };
    }
    default:
      return state;
  }
};

export const getIsAccessDenied = (state: any) => state[USER_STATE_KEY].isAccessDenied;
export const getIsReadAllAllowed = (state: any) => state[USER_STATE_KEY].isReadAllAllowed;
export const getIsWriteAllAllowed = (state: any) => state[USER_STATE_KEY].isWriteAllAllowed;
export const getReadTabsPermissions = (state: any) => state[USER_STATE_KEY].readTabsPermissions;
export const getWriteTabsPermissions = (state: any) => state[USER_STATE_KEY].writeTabsPermissions;
export const getWriteMenuPermissions = (state: any) => state[USER_STATE_KEY].writeMenuPermissions;
export const getIsReadDoctorsAllowed = (state: any) => state[USER_STATE_KEY].isReadDoctorsAllowed;
export const getIsWriteDoctorsAllowed = (state: any) => state[USER_STATE_KEY].isWriteDoctorsAllowed;
export const getIsWriteContactAllowed = (state: any) => state[USER_STATE_KEY].isWriteContactAllowed;
export const getUserPositions = (state: any) => state[USER_STATE_KEY].userPositions.Positions;
export const getUserPositionsLoading = (state: any) => state[USER_STATE_KEY].userPositionsLoading;
export const checkIsDefaultScheduleChangeRequestor = (state: any) =>
  state[USER_STATE_KEY].userPositions.IsDefaultScheduleChangeRequestor || false;
export const getLoading = (state: any) => state[USER_STATE_KEY].loading;
export const getEmailGroups = (state: any) => state[USER_STATE_KEY].emailGroups;
export const getAdminEmails = (state: any) => state[USER_STATE_KEY].adminEmails;
export const getUserEmail = (state: any) => state[USER_STATE_KEY].userEmail;
export const getUserDataLoading = (state: any) => state[USER_STATE_KEY].loading;
export const getTabVisibility = (state) => state[USER_STATE_KEY].tabVisibility;
export const getUserEmailAddress = (state: any) => state[USER_STATE_KEY].userPositions?.EmailId || '';
export const getUserIsMODAdmin = (state: any) =>
  !state[USER_STATE_KEY].userPositionsLoading && state[USER_STATE_KEY].userPositions?.IsMODAdmin;

export const getIsWriteBrandedFixturesAllowed = (state: any): boolean => {
  const userState = state[USER_STATE_KEY];
  return (
    !userState.userPositionsLoading &&
    (userState.userPositions?.IsMODAdmin === true ||
      (userState.userPositions?.Permissions || []).some(
        (permission: any) => permission.Name === 'Branded_Fixtures_Write' && permission.IsActive !== false
      ))
  );
};

export const getPermittedMenus = (state: any) => state[USER_STATE_KEY].permittedMenus || [];
export const getPermittedMenuTabs = (state: any) => state[USER_STATE_KEY].permittedMenuTabs || [];
export const getPermittedFeatures = (state: any) => state[USER_STATE_KEY].permittedFeatures || [];
